/**
 * Pending Orders List
 * @see Sprint 28 - US-28.5
 */

import { useNavigate } from 'react-router-dom';
import { ShoppingCart, ChevronRight } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter, Badge, Skeleton, EmptyState } from '@/components/ui';
import { useCanSeeFinancials, useCurrency, useFormatDate } from '@/hooks';
import { formatCentimes } from '@/lib/format';
import { usePendingOrders } from '../hooks';

const STATUS_BADGES: Record<string, { label: string; variant: 'warning' | 'primary' }> = {
  SENT: { label: 'Envoyee', variant: 'warning' },
  PARTIALLY_RECEIVED: { label: 'Partielle', variant: 'primary' },
};

export function PendingOrdersList() {
  const navigate = useNavigate();
  const canSeeFinancials = useCanSeeFinancials();
  const currency = useCurrency();
  const { formatDateShort } = useFormatDate();
  const { data, isLoading, isError } = usePendingOrders();

  if (isError) return null;

  const items = data?.data ?? [];
  const total = data?.pagination?.total ?? 0;

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Commandes en attente</CardTitle>
            <CardDescription>A valider ou receptionner</CardDescription>
          </div>
          {total > 0 && (
            <Badge variant="primary">{total}</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-20" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={ShoppingCart}
            title="Aucune commande en attente"
            size="sm"
          />
        ) : (
          <div className="space-y-2">
            {items.map((order) => {
              const badge = STATUS_BADGES[order.status];
              return (
                <div
                  key={order.id}
                  className="rounded-lg border border-neutral-200 p-3 hover:bg-neutral-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/achats/commandes/${order.id}`)}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-sm text-dark-500">
                      {order.orderNumber}
                    </span>
                    {badge && (
                      <Badge variant={badge.variant}>{badge.label}</Badge>
                    )}
                  </div>
                  <div className="text-sm text-neutral-500 mb-1">
                    {order.supplier.name}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-neutral-500">
                    {canSeeFinancials && (
                      <>
                        <span>{formatCentimes(order.totalExclTax, currency)}</span>
                        <span>·</span>
                      </>
                    )}
                    <span>{order.itemsCount} articles</span>
                    <span>·</span>
                    <span>{formatDateShort(order.orderDate)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
      {total > 0 && (
        <CardFooter>
          <button
            className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
            onClick={() => navigate('/achats/commandes?status=SENT')}
          >
            Voir toutes les commandes
            <ChevronRight className="h-4 w-4" />
          </button>
        </CardFooter>
      )}
    </Card>
  );
}
